import moment from 'moment';
import * as XLSX from 'xlsx';

/**
 * Line Item Field Definitions matching Ad Ops CSV format
 * These are the exact column names from their Budget & Strategy exports
 */
export const LINE_ITEM_FIELDS = {
  name: {
    key: 'name',
    label: 'Line Item Name',
    required: true,
    type: 'string',
    csvColumns: ['Line Item Name', 'Name', 'LI Name', 'Line Item'],
  },
  startDate: {
    key: 'startDate',
    label: 'Start Date',
    required: false,
    type: 'date',
    csvColumns: ['Start Date', 'Start', 'Flight Start', 'Begin Date'],
  },
  endDate: {
    key: 'endDate',
    label: 'End Date',
    required: false,
    type: 'date',
    csvColumns: ['End Date', 'End', 'Flight End', 'Finish Date'],
  },
  budgetTotal: {
    key: 'budgetTotal',
    label: 'Budget',
    required: false,
    type: 'number',
    csvColumns: ['DSP Spend', 'Budget', 'Spend', 'Amount', 'Budget Total'],
  },
  budgetPercentage: {
    key: 'budgetPercentage',
    label: 'Allocation %',
    required: false,
    type: 'number',
    csvColumns: ['Allocation%', 'Allocation', 'Budget %', 'Percentage', '%'],
  },
  cpm: {
    key: 'cpm',
    label: 'Bid/CPM',
    required: false,
    type: 'number',
    csvColumns: ['Bid', 'CPM', 'Rate', 'Cost Per Mille'],
  },
  segments: {
    key: 'segments',
    label: 'Segments',
    required: false,
    type: 'string',
    csvColumns: ['Segments', 'Segment'],
  },
  segmentId: {
    key: 'segmentId',
    label: 'Segment ID',
    required: false,
    type: 'string',
    csvColumns: ['Segment ID', 'SegmentID', 'Segment Id'],
  },
  dealName: {
    key: 'dealName',
    label: 'Deal Name',
    required: false,
    type: 'string',
    csvColumns: ['Deal Name', 'Deal Names', 'Deals'],
  },
  dealId: {
    key: 'dealId',
    label: 'Deal ID',
    required: false,
    type: 'string',
    csvColumns: ['ID', 'Deal ID', 'Deal IDs', 'DealID'],
  },
};

/**
 * Auto-detect column mappings based on CSV headers
 * Matches headers to field definitions using exact and fuzzy matching
 */
export const autoDetectColumnMappings = (headers) => {
  const mappings = {};

  headers.forEach((header) => {
    const normalizedHeader = header.toLowerCase().trim();
    let matchedField = null;
    let confidence = 0;

    // Try each field definition
    Object.entries(LINE_ITEM_FIELDS).forEach(([fieldKey, fieldDef]) => {
      // Check each possible CSV column name
      fieldDef.csvColumns.forEach((csvCol) => {
        const normalizedCol = csvCol.toLowerCase().trim();

        // Exact match
        if (normalizedHeader === normalizedCol) {
          if (confidence < 1.0) {
            matchedField = fieldKey;
            confidence = 1.0;
          }
        }
        // Header contains the column name
        else if (normalizedHeader.includes(normalizedCol) || normalizedCol.includes(normalizedHeader)) {
          const similarity = Math.min(normalizedHeader.length, normalizedCol.length) /
                           Math.max(normalizedHeader.length, normalizedCol.length);
          if (similarity * 0.9 > confidence) {
            matchedField = fieldKey;
            confidence = similarity * 0.9;
          }
        }
      });
    });

    mappings[header] = {
      field: matchedField,
      confidence: confidence,
    };
  });

  return mappings;
};

/**
 * Generate Excel template file for line item import
 */
export const downloadLineItemTemplate = () => {
  const headers = [
    'Line Item Name',
    'Start Date',
    'End Date',
    'DSP Spend',
    'Allocation%',
    'Bid',
    'Segments',
    'Segment ID',
    'Deal Name',
    'ID',
  ];

  const tomorrow = moment().add(1, 'days').format('M/D/YYYY');
  const nextMonth = moment().add(30, 'days').format('M/D/YYYY');

  const sampleRows = [
    [
      'CPM_BT_Example_DIS_Prospecting',
      tomorrow,
      nextMonth,
      '$500.00',
      '25%',
      '$8.00',
      'ExampleSegment_21Plus',
      '1234567890',
      'Example_PMP_Deal',
      'PM-XXXX-0001',
    ],
    [
      'CPM_BT_AnotherExample_DIS_Prospecting',
      tomorrow,
      nextMonth,
      '$300.00',
      '15%',
      '$7.50',
      '',
      '',
      '',
      '',
    ],
  ];

  const wsData = [headers, ...sampleRows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const columnWidths = [
    { wch: 45 }, // Line Item Name
    { wch: 12 }, // Start Date
    { wch: 12 }, // End Date
    { wch: 12 }, // DSP Spend
    { wch: 12 }, // Allocation%
    { wch: 10 }, // Bid
    { wch: 40 }, // Segments
    { wch: 15 }, // Segment ID
    { wch: 50 }, // Deal Name
    { wch: 20 }, // ID
  ];
  ws['!cols'] = columnWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Line Item Import');

  XLSX.writeFile(wb, `line_item_import_template_${moment().format('YYYY-MM-DD')}.xlsx`);
};

/**
 * Parse CSV or Excel file and return array of row objects
 */
export const parseLineItemFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    reader.onload = (e) => {
      try {
        let headers = [];
        let dataRows = [];

        if (isExcel) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });

          if (jsonData.length < 2) {
            reject(new Error('File is empty or contains only headers'));
            return;
          }

          headers = jsonData[0].map(h => String(h).trim());
          dataRows = jsonData.slice(1);
        } else {
          const text = e.target.result;
          const lines = text.split('\n').filter((line) => {
            const trimmed = line.trim();
            return trimmed && !trimmed.startsWith('#');
          });

          if (lines.length < 2) {
            reject(new Error('File is empty or contains only headers'));
            return;
          }

          headers = parseCSVLine(lines[0]);
          dataRows = lines.slice(1).map(line => parseCSVLine(line));
        }

        // Auto-detect column mappings
        const columnMappings = autoDetectColumnMappings(headers);

        const rows = [];
        dataRows.forEach((values, index) => {
          // Skip empty rows or rows that start with underscores (separator rows)
          const firstValue = String(values[0] || '').trim();
          if (!firstValue || firstValue.startsWith('_')) {
            return;
          }

          if (values && values.some((val) => String(val).trim())) {
            const rowData = {};
            headers.forEach((header, headerIndex) => {
              let cellValue = String(values[headerIndex] || '').trim();
              rowData[header] = cellValue;
            });
            rows.push({ lineNumber: index + 2, data: rowData });
          }
        });

        if (rows.length === 0) {
          reject(new Error('File contains no valid data rows'));
          return;
        }

        resolve({ rows, headers, columnMappings });
      } catch (error) {
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
};

/**
 * Parse a single CSV line handling quoted values
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result.map((val) => val.trim());
};

/**
 * Parse date in supported formats only (M/D/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * Returns null for unrecognized formats to avoid ambiguous parsing
 */
const parseDate = (dateStr) => {
  if (!dateStr) return null;

  const trimmed = String(dateStr).trim();

  // Try M/D/YYYY or MM/DD/YYYY format first (Ad Ops format)
  const mdyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    const parsed = moment(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }

  // Try YYYY-MM-DD format (ISO format)
  const ymdMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const parsed = moment(trimmed, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }

  // No fallback - return null for unrecognized formats
  return null;
};

/**
 * Parse currency/number value (handles $, %, commas)
 */
const parseNumber = (value) => {
  if (!value) return null;
  const cleaned = String(value).replace(/[$,%\s]/g, '').replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

/**
 * Validate a single line item row
 */
export const validateLineItemRow = (rowData, lineNumber, columnMappings) => {
  const errors = [];
  const warnings = [];

  const getMappedValue = (fieldKey) => {
    const csvColumn = Object.keys(columnMappings).find(
      col => columnMappings[col]?.field === fieldKey
    );
    return csvColumn ? rowData[csvColumn]?.trim() : null;
  };

  // Validate name (required)
  const name = getMappedValue('name');
  if (!name) {
    errors.push(`Row ${lineNumber}: Line Item Name is required`);
  }

  // Validate dates
  const startDateStr = getMappedValue('startDate');
  if (startDateStr) {
    const startDate = parseDate(startDateStr);
    if (!startDate || !startDate.isValid()) {
      errors.push(`Row ${lineNumber}: Invalid Start Date "${startDateStr}"`);
    }
  }

  const endDateStr = getMappedValue('endDate');
  if (endDateStr) {
    const endDate = parseDate(endDateStr);
    if (!endDate || !endDate.isValid()) {
      errors.push(`Row ${lineNumber}: Invalid End Date "${endDateStr}"`);
    }
  }

  // Validate budget
  const budgetStr = getMappedValue('budgetTotal');
  if (budgetStr) {
    const budget = parseNumber(budgetStr);
    if (budget === null) {
      errors.push(`Row ${lineNumber}: Invalid Budget "${budgetStr}"`);
    }
  }

  // Validate CPM/Bid
  const cpmStr = getMappedValue('cpm');
  if (cpmStr) {
    const cpm = parseNumber(cpmStr);
    if (cpm === null) {
      errors.push(`Row ${lineNumber}: Invalid Bid/CPM "${cpmStr}"`);
    }
  }

  return { errors, warnings };
};

/**
 * Validate all rows
 */
export const validateAllRows = (rows, columnMappings) => {
  const allErrors = [];
  const allWarnings = [];

  rows.forEach((row) => {
    const { errors, warnings } = validateLineItemRow(row.data, row.lineNumber, columnMappings);
    allErrors.push(...errors);
    allWarnings.push(...warnings);
  });

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};

/**
 * Transform parsed row data to line item objects
 */
export const transformToLineItems = (rows, columnMappings, campaignDefaults = {}) => {
  const {
    campaignStartDate,
    campaignEndDate,
    defaultCpm = 7,
    inheritedValueAdd = false,
  } = campaignDefaults;

  // Helper to get mapped value
  const getMappedValue = (rowData, fieldKey) => {
    const csvColumn = Object.keys(columnMappings).find(
      col => columnMappings[col]?.field === fieldKey
    );
    return csvColumn ? rowData[csvColumn]?.trim() : null;
  };

  return rows.map((row, index) => {
    const { data } = row;

    // Parse basic fields
    const name = getMappedValue(data, 'name') || `Imported Line Item ${index + 1}`;

    const startDateStr = getMappedValue(data, 'startDate');
    const parsedStartDate = parseDate(startDateStr);
    const startDate = parsedStartDate && parsedStartDate.isValid()
      ? parsedStartDate.utc().startOf('day')
      : campaignStartDate
        ? moment(campaignStartDate).utc().startOf('day')
        : moment().utc().startOf('day');

    const endDateStr = getMappedValue(data, 'endDate');
    const parsedEndDate = parseDate(endDateStr);
    const endDate = parsedEndDate && parsedEndDate.isValid()
      ? parsedEndDate.utc().endOf('day')
      : campaignEndDate
        ? moment(campaignEndDate).utc().endOf('day')
        : moment().add(30, 'days').utc().endOf('day');

    const budgetTotal = parseNumber(getMappedValue(data, 'budgetTotal')) || 0;
    const budgetPercentage = parseNumber(getMappedValue(data, 'budgetPercentage')) || 0;
    const cpm = parseNumber(getMappedValue(data, 'cpm')) || defaultCpm;

    // Parse segments (can be comma-separated)
    const segmentNames = getMappedValue(data, 'segments');
    const segmentIds = getMappedValue(data, 'segmentId');
    const nexxenSegments = [];

    if (segmentNames && segmentIds) {
      const names = segmentNames.split(',').map(s => s.trim()).filter(Boolean);
      const ids = segmentIds.split(',').map(s => s.trim()).filter(Boolean);

      // Match names with IDs (they should be in order)
      names.forEach((segName, i) => {
        if (ids[i]) {
          nexxenSegments.push({
            segmentId: ids[i],
            name: segName,
            segmentType: 'THIRD_PARTY',
            cost: 0,
          });
        }
      });
    }

    // Parse deal IDs (can be comma-separated)
    const dealNames = getMappedValue(data, 'dealName');
    const dealIds = getMappedValue(data, 'dealId');
    const dspDealIds = [];

    if (dealNames && dealIds) {
      const names = dealNames.split(',').map(s => s.trim()).filter(Boolean);
      const ids = dealIds.split(',').map(s => s.trim()).filter(Boolean);

      // Match names with IDs
      names.forEach((dealName, i) => {
        if (ids[i]) {
          dspDealIds.push({
            dealId: ids[i],
            name: dealName,
            cpmBid: null,
          });
        }
      });
    }

    // Build line item object
    const lineItem = {
      name,
      startDate,
      endDate,
      budgetTotal,
      budgetPercentage,
      cpm,
      regionGroups: [],
      targetingGroups: [],
      targetingType: null,
      targetingRegions: [],
      targetingLocations: [],
      targetingCountries: null,
      audienceType: nexxenSegments.length > 0 ? 'THIRD_PARTY' : null,
      firstPartyDataAudiences: [],
      adGroup: null,
      valueAdd: inheritedValueAdd,
      nexxenSegments,
      dspDealIds,
    };

    return lineItem;
  });
};
